import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, CheckCircle2, Calendar as CalIcon, Clock, Sunset, Users } from "lucide-react";
import { ConsentNote } from "../components/legal/LegalBits";
import { PendingConfirmationAlert } from "../components/PendingConfirmationAlert";
import { SubPageNav } from "../components/SubPageNav";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { fetchRestaurant } from "../lib/api";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";
import { tr } from "../lib/i18n";
import AppBottomBar from "../components/AppBottomBar";

const H = {
  noSlot: { it: "Nessuno slot disponibile", en: "No slot available", es: "Sin turno disponible", fr: "Aucun créneau disponible", de: "Kein Termin verfügbar" },
  tryOther: { it: "Prova un'altra data", en: "Try another date", es: "Prueba otra fecha", fr: "Essayez une autre date", de: "Versuchen Sie ein anderes Datum" },
  cantBook: { it: "Impossibile prenotare", en: "Booking failed", es: "No se pudo reservar", fr: "Réservation impossible", de: "Buchung fehlgeschlagen" },
  unexpected: { it: "Errore imprevisto. Riprova.", en: "Unexpected error. Please retry.", es: "Error inesperado. Inténtalo de nuevo.", fr: "Erreur inattendue. Réessayez.", de: "Unerwarteter Fehler. Bitte erneut versuchen." },
  received: { it: "Richiesta ricevuta", en: "Request received", es: "Solicitud recibida", fr: "Demande reçue", de: "Anfrage erhalten" },
  thanks: { it: "Grazie", en: "Thank you", es: "Gracias", fr: "Merci", de: "Danke" },
  willContact: { it: "Ti contatteremo per confermare.", en: "We'll contact you to confirm.", es: "Te contactaremos para confirmar.", fr: "Nous vous contacterons pour confirmer.", de: "Wir melden uns zur Bestätigung." },
  slot: { it: "Slot", en: "Slot", es: "Turno", fr: "Créneau", de: "Termin" },
  sunset: { it: "tramonto", en: "sunset", es: "atardecer", fr: "coucher du soleil", de: "Sonnenuntergang" },
  person: { it: "persona", en: "person", es: "persona", fr: "personne", de: "Person" },
  people: { it: "persone", en: "people", es: "personas", fr: "personnes", de: "Personen" },
  total: { it: "Totale", en: "Total", es: "Total", fr: "Total", de: "Gesamt" },
  payOnSite: { it: "da saldare in struttura", en: "to be paid on site", es: "a pagar en el establecimiento", fr: "à régler sur place", de: "vor Ort zu zahlen" },
  backHome: { it: "Torna alla home", en: "Back to home", es: "Volver al inicio", fr: "Retour à l'accueil", de: "Zur Startseite" },
  atSunset: { it: "Al tramonto · 2 ore", en: "At sunset · 2 hours", es: "Al atardecer · 2 horas", fr: "Au coucher du soleil · 2 h", de: "Bei Sonnenuntergang · 2 Std." },
  bookYour: { it: "Prenota il tuo", en: "Book your", es: "Reserva tu", fr: "Réservez votre", de: "Buchen Sie Ihren" },
  horseRide: { it: "Giro a Cavallo", en: "Horseback Ride", es: "Paseo a Caballo", fr: "Balade à Cheval", de: "Ausritt" },
  heroText: {
    it: "Passeggiata al tramonto per 1 o 2 persone tra i sentieri delle colline marchigiane. Prenota il tuo turno e paga in struttura all'arrivo.",
    en: "Sunset ride for 1 or 2 people along the trails of the Marche hills. Book your slot and pay on site at arrival.",
    es: "Paseo al atardecer para 1 o 2 personas por los senderos de las colinas de las Marcas. Reserva tu turno y paga a la llegada.",
    fr: "Balade au coucher du soleil pour 1 ou 2 personnes sur les sentiers des collines des Marches. Réservez votre créneau et payez sur place.",
    de: "Ausritt bei Sonnenuntergang für 1 oder 2 Personen auf den Wegen der Marken-Hügel. Buchen Sie Ihren Termin und zahlen Sie vor Ort.",
  },
  experience: { it: "Esperienza", en: "Experience", es: "Experiencia", fr: "Expérience", de: "Erlebnis" },
  rideName: { it: "Cavalcata al tramonto · 2 ore", en: "Sunset ride · 2 hours", es: "Paseo al atardecer · 2 horas", fr: "Balade au coucher du soleil · 2 h", de: "Ausritt bei Sonnenuntergang · 2 Std." },
  perPerson: { it: "a persona", en: "per person", es: "por persona", fr: "par personne", de: "pro Person" },
  date: { it: "Data", en: "Date", es: "Fecha", fr: "Date", de: "Datum" },
  shift: { it: "Turno", en: "Slot", es: "Turno", fr: "Créneau", de: "Termin" },
  checking: { it: "Verifica disponibilità…", en: "Checking availability…", es: "Comprobando disponibilidad…", fr: "Vérification de la disponibilité…", de: "Verfügbarkeit wird geprüft…" },
  sunsetCap: { it: "Tramonto", en: "Sunset", es: "Atardecer", fr: "Coucher du soleil", de: "Sonnenuntergang" },
  available: { it: "Disponibile", en: "Available", es: "Disponible", fr: "Disponible", de: "Verfügbar" },
  noShift: { it: "Nessun turno disponibile in questa data. Prova un altro giorno.", en: "No slot available on this date. Try another day.", es: "Sin turno disponible en esta fecha. Prueba otro día.", fr: "Aucun créneau disponible à cette date. Essayez un autre jour.", de: "Kein Termin an diesem Datum. Versuchen Sie einen anderen Tag." },
  peopleCap: { it: "Persone", en: "People", es: "Personas", fr: "Personnes", de: "Personen" },
  fullName: { it: "Nome e cognome", en: "Full name", es: "Nombre y apellido", fr: "Nom et prénom", de: "Vor- und Nachname" },
  phone: { it: "Telefono (+39...)", en: "Phone (+44...)", es: "Teléfono (+34...)", fr: "Téléphone (+33...)", de: "Telefon (+49...)" },
  notes: { it: "Note (esperienza, allergie, bambini…)", en: "Notes (experience, allergies, children…)", es: "Notas (experiencia, alergias, niños…)", fr: "Notes (expérience, allergies, enfants…)", de: "Hinweise (Erfahrung, Allergien, Kinder…)" },
  totalOnSite: { it: "Totale da pagare in struttura", en: "Total to pay on site", es: "Total a pagar en el establecimiento", fr: "Total à régler sur place", de: "Gesamtbetrag vor Ort" },
  booking: { it: "Prenotazione in corso…", en: "Booking…", es: "Reservando…", fr: "Réservation…", de: "Buchung läuft…" },
  bookNow: { it: "Prenota adesso", en: "Book now", es: "Reservar ahora", fr: "Réserver", de: "Jetzt buchen" },
  footer: { it: "Confermando la prenotazione ti contatteremo su WhatsApp. Pagamento in struttura all'arrivo.", en: "After booking we'll contact you on WhatsApp. Payment on site at arrival.", es: "Tras la reserva te contactaremos por WhatsApp. Pago en el establecimiento a la llegada.", fr: "Après la réservation, nous vous contacterons sur WhatsApp. Paiement sur place à l'arrivée.", de: "Nach der Buchung kontaktieren wir Sie über WhatsApp. Zahlung vor Ort bei Ankunft." },
};
const LOCALE = { it: "it-IT", en: "en-GB", es: "es-ES", fr: "fr-FR", de: "de-DE" };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function maxISO() {
  const d = new Date();
  d.setDate(d.getDate() + 60);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateIT(iso, lang = "it") {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(LOCALE[lang] || "it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

const PRICE = 30;

export default function HorseRidePage() {
  const navigate = useNavigate();
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "it");
  const setLang = (l) => { localStorage.setItem("lang", l); setLangState(l); };
  const t = (k) => tr(H[k], lang);
  const [restaurant, setRestaurant] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [slot, setSlot] = useState(null);
  const [loadingSlot, setLoadingSlot] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", people: 1, notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { fetchRestaurant().then(setRestaurant).catch(() => {}); }, []);

  useEffect(() => {
    let alive = true;
    setLoadingSlot(true);
    axios
      .get(`${API}/horse-ride/slot`, { params: { date } })
      .then((r) => alive && setSlot(r.data))
      .catch(() => alive && setSlot(null))
      .finally(() => alive && setLoadingSlot(false));
    return () => { alive = false; };
  }, [date]);

  const total = useMemo(() => PRICE * (form.people || 1), [form.people]);
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!slot || slot.available === false) {
      toast({ title: t("noSlot"), description: t("tryOther") });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/horse-ride/book`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        date,
        people: Number(form.people),
        notes: form.notes.trim(),
      });
      setSuccess({ booking: { ...form, price: data.total }, slot: data.slot, date_label: formatDateIT(date, lang), wa: data.guest_wa_url || data.admin_wa_url });
      // Open WhatsApp to the CUSTOMER so the reception can send the confirmation
      // directly (admin has already been notified server-side via manual_customers upsert).
      if (data.guest_wa_url) window.open(data.guest_wa_url, "_blank", "noopener,noreferrer");
      else if (data.admin_wa_url) window.open(data.admin_wa_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast({ title: t("cantBook"), description: err?.response?.data?.detail || t("unexpected") });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#faf3ee]">
        <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} compact stickyBar />
        <main className="max-w-2xl mx-auto px-4 py-10">
          <div className="rounded-2xl bg-white border border-[#e8dcc8] p-8 shadow-sm text-center">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
            <h1 className="mt-4 font-serif text-3xl font-bold text-[#2b1410]">{t("received")}</h1>
            <p className="mt-2 text-[#7c5a4e]">
              {t("thanks")} <strong>{success.booking.name}</strong>! {t("willContact")}
            </p>
            <div className="mt-6 text-left rounded-xl bg-[#faf3ee] border border-[#e8dcc8] p-5 space-y-2">
              <div className="flex items-center gap-2 text-[#2b1410]"><CalIcon className="h-4 w-4 text-[#4a1c17]" /> <strong>{success.date_label}</strong></div>
              <div className="flex items-center gap-2 text-[#2b1410]"><Clock className="h-4 w-4 text-[#4a1c17]" /> {t("slot")} <strong>{success.slot.start} – {success.slot.end}</strong> ({t("sunset")} {success.slot.sunset})</div>
              <div className="flex items-center gap-2 text-[#2b1410]"><Users className="h-4 w-4 text-[#4a1c17]" /> {success.booking.people} {success.booking.people === 1 ? t("person") : t("people")}</div>
              <div className="text-[#2b1410]">{t("total")}: <strong>€ {success.booking.price}</strong> · {t("payOnSite")}</div>
            </div>
            <PendingConfirmationAlert lang={lang} phone={restaurant?.info?.phone} />
            <button
              onClick={() => navigate("/")}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-5 py-2 text-sm font-medium"
              data-testid="cavallo-back-home"
            >
              {t("backHome")}
            </button>
          </div>
        </main>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf3ee]">
      <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} compact stickyBar />
      <main className="max-w-3xl mx-auto px-4 py-6" data-testid="cavallo-booking-page">
        <button onClick={() => navigate("/")} className="hidden" data-testid="cavallo-back-btn">
          <ChevronLeft className="h-4 w-4" /> Torna ai menu
        </button>
        <SubPageNav hideMenu lang={lang} />

        {/* Hero: standalone rounded card with looping video, matching /bici layout */}
        <section className="relative overflow-hidden rounded-3xl bg-[#2b1410] text-[#f7ebdf] shadow-lg min-h-[320px] sm:min-h-[440px] flex items-center">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/media/hero-horse.mp4"
            poster="/media/hero-horse-poster.jpg"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2b1410]/95 via-[#2b1410]/55 to-[#2b1410]/25 pointer-events-none" />
          <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
               style={{ backgroundImage: "radial-gradient(rgba(255,220,180,0.4) 1px, transparent 1px)", backgroundSize: "14px 14px" }} />

          <div className="relative w-full px-6 py-10 sm:px-12 sm:py-14 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/25 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-[#e6c46a]">
              <Sunset className="h-3.5 w-3.5" /> {t("atSunset")}
            </div>
            <h1 className="mt-5 font-serif text-4xl sm:text-6xl font-bold leading-[0.95] uppercase text-[#f7ebdf] drop-shadow-lg">
              {t("bookYour")}<br /><span className="text-[#e6c46a] italic normal-case">{t("horseRide")}</span>
            </h1>
            <p className="mt-5 max-w-md mx-auto text-sm sm:text-base text-[#f7ebdf]/95 drop-shadow">
              {t("heroText")}
            </p>
          </div>
        </section>

        {/* Booking form — separated card below, matching /bici's feature cards look */}
        <div className="mt-6 rounded-2xl border border-[#e8dcc8] shadow-sm bg-white">
          <form onSubmit={submit} className="p-6 sm:p-8 space-y-5">
            {/* Trattamento */}
            <div className="rounded-lg bg-[#faf3ee] border border-[#e8dcc8] px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#7c5a4e]">{t("experience")}</div>
              <div className="mt-0.5 text-[#2b1410] font-medium">
                <Sunset className="h-4 w-4 inline mr-1 text-[#4a1c17]" /> {t("rideName")} — <strong className="text-[#4a1c17]">€ {PRICE} {t("perPerson")}</strong>
              </div>
            </div>

            {/* Data */}
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">
                <CalIcon className="h-3.5 w-3.5 inline mr-1" /> {t("date")}
              </label>
              <input
                type="date"
                value={date}
                min={todayISO()}
                max={maxISO()}
                onChange={(e) => setDate(e.target.value)}
                required
                data-testid="cavallo-date-input"
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
              />
              {date && <p className="mt-1 text-xs text-[#7c5a4e] capitalize">{formatDateIT(date, lang)}</p>}
            </div>

            {/* Turno */}
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">
                <Clock className="h-3.5 w-3.5 inline mr-1" /> {t("shift")}
              </label>
              {loadingSlot ? (
                <div className="flex items-center gap-2 text-sm text-[#7c5a4e] py-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("checking")}
                </div>
              ) : slot && slot.available !== false ? (
                <div className="w-full rounded-lg border-2 border-[#4a1c17] bg-[#faf3ee] px-3 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#7c5a4e]">{t("sunsetCap")} {slot.sunset}</div>
                      <div className="text-lg font-serif font-bold text-[#4a1c17]">{slot.start} – {slot.end}</div>
                    </div>
                    <div className="text-[11px] text-emerald-700 font-semibold">{t("available")}</div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-red-600 py-2">{t("noShift")}</div>
              )}
            </div>

            {/* Persone */}
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">
                <Users className="h-3.5 w-3.5 inline mr-1" /> {t("peopleCap")}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[1, 2].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setForm((f) => ({ ...f, people: n }))}
                    data-testid={`cavallo-people-${n}`}
                    className={`rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-colors ${
                      form.people === n
                        ? "border-[#4a1c17] bg-[#4a1c17] text-white"
                        : "border-[#e8dcc8] bg-white text-[#2b1410] hover:bg-[#faf3ee]"
                    }`}
                  >
                    {n} {n === 1 ? t("person") : t("people")} · € {PRICE * n}
                  </button>
                ))}
              </div>
            </div>

            {/* Nome / Email / Tel / Note */}
            <div className="grid grid-cols-1 gap-3">
              <input
                type="text" placeholder={t("fullName")} required
                value={form.name} onChange={on("name")}
                data-testid="cavallo-name"
                className="rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
              />
              <input
                type="email" placeholder="Email"
                value={form.email} onChange={on("email")}
                data-testid="cavallo-email"
                className="rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
              />
              <input
                type="tel" placeholder={t("phone")} required
                value={form.phone} onChange={on("phone")}
                data-testid="cavallo-phone"
                className="rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
              />
              <textarea
                placeholder={t("notes")}
                value={form.notes} onChange={on("notes")}
                data-testid="cavallo-notes"
                className="rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25 min-h-[80px]"
              />
            </div>

            {/* Totale */}
            <div className="rounded-lg bg-[#4a1c17]/5 border border-[#4a1c17]/20 px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#7c5a4e]">{t("totalOnSite")}</div>
                <div className="text-[#7c5a4e] text-xs">{form.people} × € {PRICE}</div>
              </div>
              <div className="text-2xl font-bold text-[#4a1c17]">€ {total}</div>
            </div>

            <button
              type="submit"
              disabled={submitting || !slot || slot.available === false}
              data-testid="cavallo-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] disabled:opacity-50 text-[#f7ebdf] px-4 py-3 text-sm font-semibold shadow-sm transition-colors"
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("booking")}</> : t("bookNow")}
            </button>
            <ConsentNote action="Prenotando" />
            <p className="text-[11px] text-[#7c5a4e] text-center leading-snug">
              {t("footer")}
            </p>
          </form>
        </div>
      </main>
      <AppBottomBar current="none" lang={lang} />
      <Toaster />
    </div>
  );
}
